import { useState, useCallback } from "react";

/**
 * Custom hook for managing transaction selection state
 * @description Handles multi-select functionality for transaction tables
 * @returns Selection state and handlers
 */
export function useTransactionSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /**
   * Toggles selection for a single transaction
   */
  const toggleSelection = useCallback((id: string) => {
    setSelected(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  /**
   * Selects all provided transaction IDs
   */
  const selectAll = useCallback((ids: string[]) => {
    setSelected(new Set(ids));
  }, []);

  /**
   * Clears all selections
   */
  const clearSelection = useCallback(() => {
    setSelected(new Set());
  }, []);

  /**
   * Checks if a transaction is selected
   */
  const isSelected = useCallback((id: string) => {
    return selected.has(id);
  }, [selected]);

  /**
   * Gets count of selected items
   */
  const selectedCount = selected.size;

  return {
    selected,
    setSelected,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    selectedCount
  };
}
